import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Carousel from 'nuka-carousel';
import cx from 'classnames';

import { PREVIOUS_IMAGE_BUTTON_CLICK, NEXT_IMAGE_BUTTON_CLICK } from 'lib/analytics/events';
import { imageCdnAsset } from 'lib/common/url-helpers';

const propTypes = {
  urls: PropTypes.arrayOf(PropTypes.string),
  alts: PropTypes.arrayOf(PropTypes.string),
  slide: PropTypes.number,
  height: PropTypes.number,
  maxHeight: PropTypes.number,
  width: PropTypes.number,
  message: PropTypes.node,
  trackEvent: PropTypes.func,
};

const defaultProps = {
  urls: null,
  alts: [],
  slide: 0,
  height: 300,
  maxHeight: 300,
  width: undefined,
  message: null,
  trackEvent: () => {},
};

const SlideProgress = ({ slideCount, currentSlide }) => {
  if (slideCount > 1) {
    return (
      <div className="gallery-data">
        <img src={imageCdnAsset('/images/svg/picture-icon.svg')} alt="" />
        {currentSlide + 1}/{slideCount}
      </div>
    );
  }
  return null;
};

SlideProgress.propTypes = {
  slideCount: PropTypes.number,
  currentSlide: PropTypes.number,
};

SlideProgress.defaultProps = {
  slideCount: 0,
  currentSlide: 0,
};

const renderCarouselStyles = height => ({
  __html: `
    .slider-list { height: ${height}px !important; }
    .slider-slide { height: 100% !important; }
    .slider-slide img { max-height: ${height}px !important; object-fit: cover !important; height: 100% !important; }
  `,
});

const SimpleCarousel = ({
  urls,
  alts,
  slide,
  height: rawHeight,
  maxHeight,
  width,
  message,
  trackEvent,
}) => {
  const decorators = useMemo(() => {
    const PreviousArrowDecorator = ({ slideCount, previousSlide }) => {
      if (slideCount > 1) {
        return (
          <span
            className="iconified-font iconified-thin-chevron-left text-size-42 text-color-white text-shadow-light"
            onClick={() => {
              previousSlide();
              trackEvent(PREVIOUS_IMAGE_BUTTON_CLICK);
            }}
          />
        );
      }
      return null;
    };

    const NextArrowDecorator = ({ slideCount, nextSlide }) => {
      if (slideCount > 1) {
        return (
          <span
            className="iconified-font iconified-thin-chevron-right text-size-42 text-color-white text-shadow-light"
            onClick={() => {
              nextSlide();
              trackEvent(NEXT_IMAGE_BUTTON_CLICK);
            }}
          />
        );
      }
      return null;
    };

    const MessageTagDecorator = () => {
      const classes = cx({
        'floating-tag': true,
        'wrap-ellipses': true,
        hidden: !message,
      });
      return (
        <div className={classes}>
          {message}
        </div>
      );
    };

    return [
      {
        component: PreviousArrowDecorator,
        position: 'CenterLeft',
        style: { padding: 20 },
      },
      {
        component: NextArrowDecorator,
        position: 'CenterRight',
        style: { padding: 20 },
      },
      {
        component: SlideProgress,
        position: 'BottomCenter',
        style: { padding: 20 },
      },
      {
        component: MessageTagDecorator,
        position: 'TopRight',
        style: { maxWidth: '90%' },
      },
    ];
  }, [message, trackEvent]);

  if (!urls) {
    return null;
  }

  const height = Math.min(rawHeight, maxHeight);

  return (
    <div>
      <style dangerouslySetInnerHTML={renderCarouselStyles(height)} />
      <Carousel
        className="image-gallery"
        decorators={decorators}
        dragging={urls.length > 1}
        slideIndex={slide}
        wrapAround
        initialSlideHeight={height}
        initialSlideWidth={width}
      >
        {urls.map((url, i) => (
          <img key={url || i} src={url} alt={(alts && alts[i]) || ''} />
        ))}
      </Carousel>
    </div>
  );
};

SimpleCarousel.propTypes = propTypes;
SimpleCarousel.defaultProps = defaultProps;

export default SimpleCarousel;
